import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNavBar from "@/components/BottomNavBar";
import WhySmartBanner from "@/components/WhySmartBanner";
import AdBanner from "@/components/AdBanner";
import MarketplaceBulkImport from "@/components/MarketplaceBulkImport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MapPin, Eye, Tag, Plus, Search, MessageCircle, Trash2, CheckCircle2, Flame, Truck, Megaphone } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
    { id: "clothing", label: "Clothing" },
    { id: "furniture", label: "Furniture" },
    { id: "electronics", label: "Electronics" },
    { id: "home-goods", label: "Home Goods" },
    { id: "baby-kids", label: "Baby & Kids" },
    { id: "appliances", label: "Appliances" },
    { id: "beauty", label: "Beauty" },
    { id: "sports", label: "Sports & Fitness" },
    { id: "tools", label: "Tools" },
    { id: "vehicles-parts", label: "Vehicles & Parts" },
    { id: "jewelry", label: "Jewelry" },
    { id: "other", label: "Other" },
];

const CONDITIONS = [
    { id: "new", label: "Brand new" },
    { id: "like-new", label: "Like new" },
    { id: "good", label: "Good" },
    { id: "fair", label: "Fair" },
];

export default function MarketplacePage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [filters, setFilters] = useState({
        category: params.get("category") || "",
        city: params.get("city") || "",
        q: params.get("q") || "",
        sort: "newest",
    });

    const refresh = async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            if (filters.category) q.set("category", filters.category);
            if (filters.city) q.set("city", filters.city);
            if (filters.q) q.set("q", filters.q);
            if (filters.sort) q.set("sort", filters.sort);
            const { data } = await api.get(`/marketplace/listings?${q}`);
            setListings(data || []);
        } catch {
            setListings([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.category, filters.city, filters.sort]);

    const onSearch = (e) => {
        e.preventDefault();
        refresh();
    };

    const handleListClick = () => {
        if (!user) {
            toast.message("Sign in to list items");
            navigate("/auth?next=/marketplace");
            return;
        }
        setShowNew(true);
    };

    return (
        <div className="min-h-screen bg-background pb-24" data-testid="marketplace-page">
            <Header />
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 lg:pt-16">
                <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
                    <div>
                        <div className="overline mb-3">The Royal Marketplace</div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
                            Buy &amp; sell <span className="italic gold-grad">in Jamaica.</span>
                        </h1>
                        <p className="mt-4 text-muted-foreground max-w-xl leading-relaxed">
                            New, pre-owned, used — list clothes, furniture, electronics, baby gear,
                            tools or jewelry. Buyers message you direct. Need it delivered?
                            Tap <span className="gold-text">SMART Driver</span> and we'll take it door-to-door.
                        </p>
                        <div className="mt-3 inline-flex items-center flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.22em] text-muted-foreground" data-testid="marketplace-fee-strip">
                            <span>· JMD $1,000 service fee per listing</span>
                            <span>· 5% commission on sale</span>
                            <span>· Boost to the top for JMD $500 / 7 days</span>
                        </div>
                    </div>
                    <Button
                        onClick={handleListClick}
                        className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-6"
                        data-testid="marketplace-list-cta"
                    >
                        <Plus size={14} className="mr-2" /> List an item
                    </Button>
                </div>

                {user && (
                    <div className="mb-6 flex flex-wrap gap-3" data-testid="marketplace-seller-tools">
                        <MarketplaceBulkImport onImported={refresh} />
                        <a
                            href="/advertise"
                            className="inline-flex items-center justify-center rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-5 border border-white/10 text-muted-foreground hover:text-foreground transition-colors"
                            data-testid="marketplace-advertise-cta"
                        >
                            <Megaphone size={14} className="mr-2" /> Advertise on SMART
                        </a>
                    </div>
                )}

                <div className="mb-8" data-testid="marketplace-ad-slot">
                    <AdBanner slot="marketplace_top" />
                </div>

                {/* Filters */}
                <form onSubmit={onSearch} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_120px] gap-3 mb-10">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={filters.q}
                            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                            placeholder="Search listings"
                            className="bg-input border-white/10 h-11 rounded-sm pl-9"
                            data-testid="marketplace-search-input"
                        />
                    </div>
                    <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
                        <SelectTrigger className="bg-input border-white/10 h-11 rounded-sm" data-testid="marketplace-category-filter">
                            <SelectValue placeholder="All categories" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Input
                        value={filters.city}
                        onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                        placeholder="City (optional)"
                        className="bg-input border-white/10 h-11 rounded-sm"
                        data-testid="marketplace-city-filter"
                    />
                    <Button type="submit" className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11">
                        Search
                    </Button>
                </form>

                {/* Grid */}
                {loading ? (
                    <div className="text-center py-16 text-muted-foreground" data-testid="marketplace-loading">Loading royal finds…</div>
                ) : listings.length === 0 ? (
                    <div className="text-center py-20 border border-white/5 bg-card/40 rounded-sm" data-testid="marketplace-empty">
                        <div className="overline mb-3">The marketplace awaits</div>
                        <h3 className="font-serif text-2xl mb-3">Be the first to list.</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Clear out, cash in — list anything from clothes to furniture in under a minute.
                        </p>
                        <Button
                            onClick={handleListClick}
                            className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11 px-6"
                            data-testid="marketplace-empty-cta"
                        >
                            List your first item
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="marketplace-grid">
                        {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
                    </div>
                )}
            </section>

            <NewListingDialog open={showNew} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); refresh(); }} />
            <WhySmartBanner />
            <Footer />
            <BottomNavBar />
        </div>
    );
}


function ListingCard({ listing }) {
    const img = (listing.images && listing.images[0]) || "https://images.unsplash.com/photo-1611117775350-ac3950990985?w=400";
    return (
        <Link
            to={`/marketplace/${listing.id}`}
            className={`group bg-card border rounded-sm overflow-hidden transition-colors ${
                listing.is_boosted
                    ? "border-[#FFD700]/60 shadow-[0_0_18px_-6px_rgba(255,215,0,0.5)] hover:border-[#FFD700]"
                    : "border-white/10 hover:border-primary/40"
            }`}
            data-testid={`listing-card-${listing.id}`}
        >
            <div className="aspect-square overflow-hidden bg-black/40 relative">
                <img src={img} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                {listing.is_boosted && (
                    <span
                        data-testid={`listing-boost-${listing.id}`}
                        className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFD700] text-black text-[9px] uppercase tracking-[0.26em] font-semibold"
                    >
                        <Flame size={9} /> Promoted
                    </span>
                )}
                {listing.delivery_available && (
                    <span
                        data-testid={`listing-delivery-${listing.id}`}
                        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/90 text-black text-[9px] uppercase tracking-[0.26em] font-semibold"
                    >
                        <Truck size={9} /> Delivery
                    </span>
                )}
            </div>
            <div className="p-3">
                <div className="font-serif text-lg truncate" data-testid={`listing-title-${listing.id}`}>{listing.title}</div>
                <div className="flex items-center justify-between mt-1">
                    <span className="gold-text text-sm font-medium">${listing.price_jmd?.toLocaleString()}</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{listing.condition}</span>
                </div>
                <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground">
                    <MapPin size={10} strokeWidth={1.5} /> {listing.city}
                    {listing.is_negotiable && <span className="text-amber-300 ml-auto">Negotiable</span>}
                </div>
            </div>
        </Link>
    );
}


function NewListingDialog({ open, onClose, onCreated }) {
    const { user } = useAuth();
    const [form, setForm] = useState({
        title: "", description: "", category: "clothing", condition: "like-new",
        price_jmd: "", city: "", parish: "", whatsapp: user?.phone || "",
        is_negotiable: false, delivery_available: false, pickup_address: "",
    });
    const [imageUrls, setImageUrls] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const price = parseFloat(form.price_jmd);
        if (!price || price <= 0) return toast.error("Enter a valid price");
        if (!form.title.trim() || form.title.length < 3) return toast.error("Title too short");
        if (!form.description.trim() || form.description.length < 10) return toast.error("Add a longer description");
        setSaving(true);
        try {
            const images = imageUrls.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 8);
            await api.post("/marketplace/listings", {
                ...form,
                price_jmd: price,
                images,
            });
            toast.success("Your Royal listing is live.");
            onCreated();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-card border-primary/30 sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="marketplace-new-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">List your item</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <Input placeholder="Title (e.g. Beige sectional couch)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-input border-white/15 h-11 rounded-sm" data-testid="listing-title-input" />
                    <Textarea placeholder="Describe your item honestly — size, color, age, any flaws." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="bg-input border-white/15 rounded-sm" data-testid="listing-description-input" />
                    <div className="grid grid-cols-2 gap-3">
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                            <SelectTrigger className="bg-input border-white/15 h-11 rounded-sm" data-testid="listing-category-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                            <SelectTrigger className="bg-input border-white/15 h-11 rounded-sm" data-testid="listing-condition-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Input type="number" placeholder="Price (JMD)" value={form.price_jmd} onChange={(e) => setForm({ ...form, price_jmd: e.target.value })} className="bg-input border-white/15 h-11 rounded-sm" data-testid="listing-price-input" />
                        <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-input border-white/15 h-11 rounded-sm" data-testid="listing-city-input" />
                    </div>
                    <Input placeholder="Your WhatsApp (+1876…)" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="bg-input border-white/15 h-11 rounded-sm" data-testid="listing-whatsapp-input" />
                    <Textarea placeholder="Image URLs (one per line) — up to 8" value={imageUrls} onChange={(e) => setImageUrls(e.target.value)} rows={3} className="bg-input border-white/15 rounded-sm text-xs" data-testid="listing-images-input" />
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={form.is_negotiable} onChange={(e) => setForm({ ...form, is_negotiable: e.target.checked })} data-testid="listing-negotiable-checkbox" />
                        Price is negotiable
                    </label>
                    <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="listing-delivery-row">
                        <input
                            type="checkbox"
                            checked={form.delivery_available}
                            onChange={(e) => setForm({ ...form, delivery_available: e.target.checked })}
                            data-testid="listing-delivery-checkbox"
                            className="mt-0.5"
                        />
                        <span>
                            <span className="text-foreground font-medium">SMART Driver delivery</span> — buyers can
                            request a SMART driver to pick up and deliver. Buyer pays distance-based transport.
                        </span>
                    </label>
                    {form.delivery_available && (
                        <Input
                            placeholder="Pickup address (so the driver finds the item)"
                            value={form.pickup_address}
                            onChange={(e) => setForm({ ...form, pickup_address: e.target.value })}
                            className="bg-input border-white/15 h-11 rounded-sm"
                            data-testid="listing-pickup-input"
                        />
                    )}
                    <div className="rounded-md border border-[#FFD700]/30 bg-[#FFD700]/5 px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-amber-300/90" data-testid="listing-fee-notice">
                        JMD $1,000 service fee at publish · 5% commission on sale · $500 Boost available after launch
                    </div>
                    <DialogFooter className="gap-2">
                        <Button type="button" variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-10">Cancel</Button>
                        <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-[10px] h-10" data-testid="listing-submit-btn">
                            {saving ? "Publishing…" : "Publish listing"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}


export function MarketplaceDetailPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const id = window.location.pathname.split("/").pop();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imgIdx, setImgIdx] = useState(0);

    useEffect(() => {
        api.get(`/marketplace/listings/${id}`)
            .then((r) => setListing(r.data))
            .catch(() => setListing(null))
            .finally(() => setLoading(false));
    }, [id]);

    const whatsappLink = useMemo(() => {
        if (!listing?.seller_whatsapp) return null;
        const clean = listing.seller_whatsapp.replace(/\D/g, "");
        const msg = encodeURIComponent(`Hi ${listing.seller_name}, I'm interested in your SMART listing "${listing.title}" ($${listing.price_jmd?.toLocaleString()} JMD). Is it still available?`);
        return `https://wa.me/${clean}?text=${msg}`;
    }, [listing]);

    const isOwner = user && listing && user.id === listing.user_id;

    const markSold = async () => {
        if (!confirm("Mark this item as sold?")) return;
        await api.post(`/marketplace/listings/${id}/sold`);
        toast.success("Marked as sold. Royal congrats.");
        setListing({ ...listing, status: "sold" });
    };
    const removeListing = async () => {
        if (!confirm("Remove this listing permanently?")) return;
        await api.delete(`/marketplace/listings/${id}`);
        toast.success("Listing removed.");
        navigate("/marketplace");
    };

    if (loading) return <div className="min-h-screen bg-background text-center py-32 text-muted-foreground" data-testid="marketplace-detail-loading">Loading…</div>;
    if (!listing) return <div className="min-h-screen bg-background text-center py-32" data-testid="marketplace-detail-notfound"><Header /><p className="text-muted-foreground mt-20">Listing not found or no longer available.</p></div>;

    const img = (listing.images && listing.images[imgIdx]) || "https://images.unsplash.com/photo-1611117775350-ac3950990985?w=800";

    return (
        <div className="min-h-screen bg-background pb-24" data-testid="marketplace-detail-page">
            <Header />
            <section className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
                <Link to="/marketplace" className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">← Back to marketplace</Link>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mt-6">
                    <div>
                        <div className="aspect-square bg-black/40 rounded-sm overflow-hidden border border-white/10">
                            <img src={img} alt={listing.title} className="w-full h-full object-cover" />
                        </div>
                        {listing.images?.length > 1 && (
                            <div className="flex gap-2 mt-3 overflow-x-auto">
                                {listing.images.map((im, i) => (
                                    <button key={`thumb-${im}`} onClick={() => setImgIdx(i)} className={`w-16 h-16 rounded-sm overflow-hidden border ${i === imgIdx ? "border-primary" : "border-white/10"}`}>
                                        <img src={im} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="overline mb-2">{listing.category} · {listing.condition}</div>
                        <h1 className="font-serif text-4xl tracking-tighter" data-testid="detail-title">{listing.title}</h1>
                        <div className="flex items-baseline gap-3 mt-4">
                            <span className="gold-text font-serif text-3xl" data-testid="detail-price">${listing.price_jmd?.toLocaleString()}</span>
                            <span className="text-xs text-muted-foreground">JMD{listing.is_negotiable && " · negotiable"}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                            <span className="flex items-center gap-1"><MapPin size={10} /> {listing.city}{listing.parish && `, ${listing.parish}`}</span>
                            <span className="flex items-center gap-1"><Eye size={10} /> {listing.views} views</span>
                        </div>
                        <p className="mt-6 text-sm leading-relaxed whitespace-pre-wrap" data-testid="detail-description">{listing.description}</p>
                        <div className="mt-8 pt-6 border-t border-white/10">
                            <div className="overline mb-2">Sold by</div>
                            <div className="font-serif text-xl">{listing.seller_name}</div>
                        </div>
                        {listing.status === "active" ? (
                            isOwner ? (
                                <>
                                    <div className="mt-6 flex gap-2">
                                        <Button onClick={markSold} className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-sm uppercase tracking-[0.2em] text-xs h-11 flex-1" data-testid="mark-sold-btn">
                                            <CheckCircle2 size={14} className="mr-2" /> Mark sold
                                        </Button>
                                        <Button onClick={removeListing} variant="outline" className="rounded-sm uppercase tracking-[0.2em] text-xs h-11" data-testid="remove-listing-btn">
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                    <Button
                                        data-testid="boost-listing-btn"
                                        onClick={async () => {
                                            if (!window.confirm("Boost this listing to the top for 7 days — JMD $500?")) return;
                                            try {
                                                // Open HandyPay checkout for the boost fee
                                                const origin = window.location.origin;
                                                const { data } = await api.post("/payments/handypay/checkout", {
                                                    boost_listing_id: listing.id,
                                                    origin_url: origin,
                                                });
                                                window.location.href = data.url;
                                            } catch (e) {
                                                toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                                            }
                                        }}
                                        className="mt-3 w-full bg-[#FFD700] text-black rounded-sm uppercase tracking-[0.2em] text-xs h-11 hover:bg-amber-300"
                                    >
                                        <Flame size={14} className="mr-2" />
                                        {listing.is_boosted ? "Extend boost 7 more days · $500" : "Boost to top · JMD $500 / 7 days"}
                                    </Button>
                                </>
                            ) : whatsappLink ? (
                                <>
                                    <a href={whatsappLink} target="_blank" rel="noreferrer" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm uppercase tracking-[0.2em] text-xs h-12 transition-colors" data-testid="contact-seller-btn">
                                        <MessageCircle size={16} /> WhatsApp the seller
                                    </a>
                                    {listing.delivery_available && (
                                        <Link
                                            to={`/services?category=Transport&from=${encodeURIComponent(listing.pickup_address || listing.city)}&note=${encodeURIComponent("Pickup marketplace item: " + listing.title)}`}
                                            data-testid="request-smart-driver-btn"
                                            className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-black border border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10 rounded-sm uppercase tracking-[0.2em] text-xs h-12 transition-colors"
                                        >
                                            <Truck size={16} /> Request SMART driver delivery
                                        </Link>
                                    )}
                                </>
                            ) : (
                                <div className="mt-6 text-xs text-muted-foreground">Seller contact not shared yet.</div>
                            )
                        ) : (
                            <div className="mt-6 bg-black/40 border border-white/10 px-4 py-3 rounded-sm uppercase tracking-[0.2em] text-xs text-muted-foreground">This item is {listing.status}.</div>
                        )}
                    </div>
                </div>
            </section>
            <Footer />
            <BottomNavBar />
        </div>
    );
}
